/**
 * The gateway's sign-in popup (#939, Feature H).
 *
 * The framed pages need the user to be signed in to the gateway, which has its own sign-in. The
 * popup opens /oauth/v2/manage/signed-in: reaching that page means sign-in worked, and it tells
 * this window and closes itself. The popup closing also counts, in case the message is lost.
 * Either way `onDone` runs once, so the caller can load its frames again.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { parseGatewayMessage, signInUrl } from "./mcp-gateway";
import { useResolvedTheme } from "./theme-context";

const POPUP_NAME = "mcp-gateway-sign-in";
const POPUP_FEATURES = "popup=yes,width=520,height=720";
const POPUP_POLL_MS = 500;

export function useGatewaySignIn(onDone: () => void) {
  const theme = useResolvedTheme();
  const popup = useRef<Window | null>(null);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const [blocked, setBlocked] = useState(false);

  const finish = useCallback(() => {
    if (poll.current) clearInterval(poll.current);
    poll.current = null;
    if (!popup.current) return;
    popup.current = null;
    onDoneRef.current();
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const parsed = parseGatewayMessage(event, popup.current);
      if (parsed?.message.type === "signed-in") finish();
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (poll.current) clearInterval(poll.current);
      poll.current = null;
    };
  }, [finish]);

  const open = useCallback(() => {
    const url = signInUrl(theme);
    if (!url) return;
    const win = window.open(url, POPUP_NAME, POPUP_FEATURES);
    if (!win) {
      setBlocked(true);
      return;
    }
    setBlocked(false);
    popup.current = win;
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(() => {
      if (popup.current?.closed) finish();
    }, POPUP_POLL_MS);
  }, [theme, finish]);

  /** The popup's address, for a link to open it in a tab when the browser blocks the popup. */
  const fallbackUrl = signInUrl(theme);
  return { open, blocked, fallbackUrl };
}
